<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref } from 'vue'

import { Grid } from '@progress/kendo-vue-grid';
import { Checkbox as KCheckBox, type CheckboxChangeEvent } from '@progress/kendo-vue-inputs'
import { SvgIcon } from '@progress/kendo-vue-common'
import { circleIcon } from '@progress/kendo-svg-icons'
import Swal from 'sweetalert2'

import { useLogger } from 'vue-logger-plugin'

import type { IAoeCart } from '@/types/CartTypes'

import { UpdateCartActive } from '@/services/CartService'
import { FormatCurrency } from '@/services/currencyFormatter'
import {
  GetCartProductSubtotal,
  GetCartTotalDiscountAmount,
} from '@/services/CartTotalsService'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'

const logger = useLogger()

const emit = defineEmits(["update-cart"]);

const guestOrdersLoader = ref<boolean>(false)
const columns = [
        {
          field: 'activateCart',
          width: resolveColumnWidth('80px', t('partyOrderEntry.ActiveCartHeader')),
          title: t('partyOrderEntry.ActiveCartHeader'),
          cell: 'activateCartCell',
        },
        {
          field: 'customerName',
          title: t('partyOrderEntry.CustomerNameHeader'),
          cell: 'customerNameCell',
          width: resolveColumnWidth('180px', t('partyOrderEntry.CustomerNameHeader'))
        },
        {
          field: 'stockIndicator',
          title: t('partyOrderEntry.PSHeader'),
          headerClassName: 'order-grid-header-stock',
          cell: 'stockIndicatorCell',
          width: resolveColumnWidth('70px', t('partyOrderEntry.PSHeader'))
        },
        {
          field: 'productSubtotal',
          title: t('partyOrderEntry.SubTotalHeader'),
          cell: 'productSubtotalCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.SubTotalHeader')),
        },
        {
          field: 'totalDiscountAmount',
          title: t('partyOrderEntry.DiscountHeader'),
          cell: 'totalDiscountAmountCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.DiscountHeader')),
          footerCell: 'footerTemplate'
        },
        {
          field: 'shippingCost',
          title: t('partyOrderEntry.ShippingCostHeader'),
          cell: 'shippingCostCell',
          width: resolveColumnWidth('120px', t('partyOrderEntry.ShippingCostHeader'))
        },
        {
          field: 'total',
          title: t('partyOrderEntry.TotalHeader'),
          cell: 'totalCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.TotalHeader')),
          footerCell: 'footerTemplateTotal'
        },
        {
          field: 'paymentTotal',
          title: t('partyOrderEntry.PaymentHeader'),
          cell: 'paymentTotalCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.PaymentHeader')),
          footerCell: 'footerTemplatePaymentTotal'
        },
        {
          field: 'balance',
          title: t('partyOrderEntry.BalanceHeader'),
          cell: 'balanceCell',
          width: resolveColumnWidth('90px', t('partyOrderEntry.BalanceHeader')),
          footerCell: 'footerTemplateBalanceTotal'
        },
      ]

const props = defineProps<{
  griddataitems: IAoeCart[]
  disableactivate: boolean
}>()

const disableActivateCheck = ref<boolean>(props.disableactivate)

function GetGuestCartsTotal() {
  return props.griddataitems.filter(x => x.isActive == true).reduce((acc, cart) => acc + cart.total, 0)
}

function GetGuestCartsPaymentTotal() {
  return props.griddataitems.filter(x => x.isActive == true).reduce((acc, cart) => acc + cart.paymentTotal, 0)
}

function GetGuestCartsBalanceTotal() {
  return props.griddataitems.filter(x => x.isActive == true).reduce((acc, cart) => acc + cart.balance, 0)
}

function activateCartCheckboxChange(event: CheckboxChangeEvent, aoeCartIdToken: string) {
  logger.debug('activateCartCheckboxChange', aoeCartIdToken, event)

  // If unchecking (deactivating) the cart, show confirmation dialog
  if (!event.value) {
    Swal.fire({
      title: t('partyOrderEntry.InactivatingCartTitle'),
      text: t('partyOrderEntry.InactivatingCartMessage') + '\n\n' + t('partyOrderEntry.InactivatingCartConfirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('buttons.Yes'),
      cancelButtonText: t('buttons.No'),
    }).then(result => {
      if (result.isConfirmed) {
        UpdateCartActive({
          aoeCartIdToken: aoeCartIdToken,
          active: event.value
        }).then(() => {
          emit("update-cart", event)
        })
      }
      // If cancelled, the checkbox will remain checked (no action needed)
    })
    return
  }

  // If checking (activating) the cart, proceed directly
  UpdateCartActive({
        aoeCartIdToken: aoeCartIdToken,
        active: event.value
    }).then(() => {
      emit("update-cart", event)
    })
}

function GetCartStockIndicator(cart: IAoeCart) : string {
  let rval = 'Available'
  for (const item of cart.items)
  {
    if (item.inventoryLevel != null)
    {
      // If OutOfStockVisible is true, treat item as available
      if (item.outOfStockVisible === true) {
        continue
      }

      if (item.inventoryLevel.inventoryLevel == 'LowStock' && rval != 'OutOfStock')
      {
        rval = 'LowStock'
      }
      else if (item.inventoryLevel.inventoryLevel == 'OutOfStock')
      {
        rval = 'OutOfStock'
      }
      else if (item.inventoryLevel.inventoryLevel == 'StopSell')
      {
        return 'StopSell'
      }
    }
  }
  return rval;
}

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Get the first active cart to determine currency
  const firstActiveCart = props.griddataitems.find(cart => cart.isActive);
  const currencyId = firstActiveCart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

</script>

<template>
    <Grid
      ref="gridCartItems"
      :data-items="props.griddataitems.filter(cart => cart.isActive)"
      :resizable="true"
      :pageable="false"
      class="guest-orders-grid"
      :columns="columns"
      :loader="guestOrdersLoader">
      <template v-slot:activateCartCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="order-grid-activate text-center">
          <KCheckBox
            :checked="props.dataItem.isActive"
            :disabled="disableActivateCheck"
            @change="activateCartCheckboxChange($event, props.dataItem.aoeCartIdToken)"
          />
        </td>
      </template>
      <template v-slot:customerNameCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="order-grid-name">
          {{ props.dataItem.customerFirstName }} {{ props.dataItem.customerLastName }}
        </td>
      </template>
      <template v-slot:stockIndicatorCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'" class="product-grid-stock-indicator">
          <SvgIcon
            v-if="GetCartStockIndicator(props.dataItem) == 'Available'"
            class="product-stock-indicator inventory-available"
            :icon="circleIcon">
          </SvgIcon>
          <SvgIcon
            v-if="GetCartStockIndicator(props.dataItem) == 'LowStock'"
            class="product-stock-indicator inventory-low-stock"
            :icon="circleIcon">
          </SvgIcon>
          <SvgIcon
            v-if="GetCartStockIndicator(props.dataItem) == 'OutOfStock'"
            class="product-stock-indicator inventory-out-of-stock"
            :icon="circleIcon">
          </SvgIcon>
          <SvgIcon
            v-if="GetCartStockIndicator(props.dataItem) == 'StopSell'"
            class="product-stock-indicator inventory-stop-sell"
            :icon="circleIcon">
          </SvgIcon>
        </td>
      </template>
      <template v-slot:productSubtotalCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(GetCartProductSubtotal(props.dataItem)) }}
        </td>
      </template>
      <template v-slot:totalDiscountAmountCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(GetCartTotalDiscountAmount(props.dataItem)) }}
        </td>
      </template>
      <template v-slot:shippingCostCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.shippingCost) }}
        </td>
      </template>
      <template v-slot:totalCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.total) }}
        </td>
      </template>
      <template v-slot:paymentTotalCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.paymentTotal) }}
        </td>
      </template>
      <template v-slot:balanceCell="{ props }">
        <td v-if="props.rowType !== 'groupHeader'">
          {{ formatCurrency(props.dataItem.balance) }}
        </td>
      </template>
      <template v-slot:footerTemplate>
          <div>{{ t('partyOrderEntry.GridFooterTotal') }}</div>
      </template>
      <template v-slot:footerTemplateTotal>
          <div class="cart-item-grid-footer-total">{{ formatCurrency(GetGuestCartsTotal()) }} </div>
      </template>
      <template v-slot:footerTemplatePaymentTotal>
          <div class="cart-item-grid-footer-payment">{{ formatCurrency(GetGuestCartsPaymentTotal()) }} </div>
      </template>
      <template v-slot:footerTemplateBalanceTotal>
          <div class="cart-item-grid-footer-subtotal">{{ formatCurrency(GetGuestCartsBalanceTotal()) }} </div>
      </template>
    </Grid>
</template>

<style lang="scss">

.order-grid-header-stock .k-column-title
{
  display: none;
}

.aoe-party-order-summary-control .k-grid-content
{
  overflow-y: hidden;
}

.aoe-party-order-summary-control .k-grid-header
{
  padding: inherit !important;
  padding-inline-end: inherit;
}

</style>
